import { notification } from 'antd'
import type React from 'react'
import { createContext, useContext } from 'react'
import styled, { createGlobalStyle } from 'styled-components'
import { theme as lightTheme } from '../../themes/light/theme'
import type { ThemeColor } from '../../themes/types'
import { Icon } from '../Icon'

type NotificationType = 'info' | 'success' | 'error'

const variants: Record<
  NotificationType,
  { colorKey: ThemeColor; icon: 'Success' | 'Info'; alt: string }
> = {
  success: { colorKey: 'semanticPositive', icon: 'Success', alt: 'Success' },
  error: { colorKey: 'semanticNegative', icon: 'Info', alt: 'Error' },
  info: { colorKey: 'darkBlue', icon: 'Info', alt: 'Info' },
}

const GlobalStylesOverride = createGlobalStyle`
  .ant-notification-notice-wrapper {
    box-shadow: none !important;
    padding: 0 !important;
    margin-bottom: 0.5rem !important;
  }

  .ant-notification-notice {
    padding: 0.5rem !important;
    border-radius: 4px !important;
  }

  .ant-notification-notice-close {
    top: 0.5rem !important;
    right: 0.5rem !important;
  }

  .np-notification-success {
    border: 1px solid ${lightTheme.colors.semanticPositive};
  }
  .np-notification-error {
    border: 1px solid ${lightTheme.colors.semanticNegative};
  }
  .np-notification-info {
    border: 1px solid ${lightTheme.colors.darkBlue};
  }

  .ant-notification-notice-wrapper:last-child .np-notification-raised {
    margin-bottom: 44px;
  }
`

const Title = styled.div<{ $colorKey: ThemeColor }>`
  color: ${({ $colorKey }) => lightTheme.colors[$colorKey]};
  font-weight: bold;
`

const Message = styled.div`
  font-size: 1rem;
  line-height: 1.5rem;
`

type OpenNotificationProps = {
  title: string
  message?: string
  duration?: number
  type?: NotificationType
  raised?: boolean
}

type NotificationContextValue = {
  openNotification: (args: OpenNotificationProps) => void
}

const NotificationContext = createContext<NotificationContextValue | null>(null)

export const NotificationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [api, contextHolder] = notification.useNotification({ stack: false })

  const openNotification = ({
    title,
    message = '',
    duration = 3,
    type = 'info',
    raised = false,
  }: OpenNotificationProps): void => {
    const variant = variants[type]

    api.open({
      title: <Title $colorKey={variant.colorKey}>{title}</Title>,
      description: <Message>{message}</Message>,
      placement: 'bottomRight',
      duration,
      icon: <Icon icon={variant.icon} alt={variant.alt} color={variant.colorKey} />,
      closeIcon: <Icon icon="Cross" alt="Dismiss" color="actionSecondary" />,
      className: `np-notification-${type}${raised ? ' np-notification-raised' : ''}`,
    })
  }

  return (
    <NotificationContext.Provider value={{ openNotification }}>
      <GlobalStylesOverride />
      {contextHolder}
      {children}
    </NotificationContext.Provider>
  )
}

export const useNotification = (): NotificationContextValue => {
  const ctx = useContext(NotificationContext)
  if (!ctx) {
    throw new Error('useNotification must be used within a NotificationProvider')
  }
  return ctx
}
